import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ensureBibleHydrated, ensureBookAvailable } from '@/lib/bible/active';
import { chapterVerseTexts } from '@/lib/scripture-anchor';
import { BIBLE_READER_V2_ENABLED } from '@/lib/feature-flags';
import { HL_DOT_HEX, HL_FAMILY_KEYS, coerceFamily } from '@/lib/highlight-utils';
import {
  familyCounts,
  highlightsForFamily,
  JOURNAL_PAGE_SIZE,
} from '@/lib/journal-aggregate';
import { useJournalSources } from '@/hooks/useJournalSources';
import BibleThemePicker from '@/components/BibleThemePicker/BibleThemePicker';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import JournalPagination from '@/components/JournalPagination/JournalPagination';
import JournalSourceState from '@/components/JournalSourceState/JournalSourceState';
import { useSettingsStore } from '@/lib/settings-store';
import type { HighlightFamily } from '@/types/highlight';
import styles from './JournalMeaningPage.module.css';

/**
 * Scripture Journal "Highlights by meaning" lens (canon-spatial redesign Phase 3,
 * §6). A leaf route (rule #10: own back Link, native browser back, NO NavStack
 * layer), v2-gated. Two modes off the optional :family param:
 *
 *  - no family  -> the family list (dot + name + count, HL_FAMILY_KEYS order);
 *  - :family    -> every highlight you marked that family, across the whole
 *                  Bible, with the verse text RESOLVED from BSB.
 *
 * Because a family spans many chapters, the detail view pages its rows
 * (JOURNAL_PAGE_SIZE) and BSB-loads only the chapters the VISIBLE rows touch.
 * A chapter whose BSB can't resolve degrades to reference-only for its highlights
 * (highlights carry no frozen text, rule #62/#76) — honest per-chapter
 * degradation; the reader's {pending|ready|failed{offline}} pattern (BLOCKER 3)
 * gates the hydration attempt, and failure still renders the rows reference-only.
 */

// Local re-implementation of the reader's offline classification (rule #4
// cross-boundary sub-rule; the reader's copy is module-private to
// BibleChapter). "Offline" is claimed ONLY when the device is definitely
// offline — a failed request while apparently online gets the generic retry
// copy instead of "connect once" (2026-08-25 honesty fix, in lockstep with
// BibleChapter + BibleBookIndex).
function isOfflineHydrationFailure(err: unknown): boolean {
  void err;
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

type LoadState =
  | { kind: 'pending' }
  | { kind: 'ready' }
  | { kind: 'failed'; offline: boolean };

export default function JournalMeaningPage() {
  const { t } = useTranslation();
  const { family = '' } = useParams();
  const { sources, state: sourceState, ready, retry: retrySources } =
    useJournalSources();
  const bibleTheme = useSettingsStore((s) => s.bibleTheme);
  // App notice stack (v0.2.0 finishing): an in-flow spacer at the top of the
  // page (above the Back link and the theme picker, so the picker's menu also
  // opens below the notices) keeps the page below the fixed notices at the
  // top; 0 while no notice shows. Each render path mounts its own spacer with
  // the same callback ref.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const detail = family !== '';
  const isValidFamily = (HL_FAMILY_KEYS as string[]).includes(family);

  const families = useMemo(
    () => familyCounts(sources.highlights),
    [sources.highlights],
  );
  const familyHighlights = useMemo(
    () =>
      isValidFamily
        ? highlightsForFamily(sources.highlights, family as HighlightFamily)
        : [],
    [sources.highlights, isValidFamily, family],
  );

  // Resolved BSB verse text, keyed by `${slug}:${chapter}:${verse}`.
  const [verseTexts, setVerseTexts] = useState<Map<string, string>>(new Map());
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'pending' });
  const [retryTick, setRetryTick] = useState(0);
  const [page, setPage] = useState(0);

  // Reset per-family state when the param changes (the route reuses this
  // component across families) via the adjust-state-during-render pattern (React
  // docs) so there's no set-state-in-effect and no stale flash.
  const [paramFamily, setParamFamily] = useState(family);
  if (paramFamily !== family) {
    setParamFamily(family);
    setPage(0);
    setVerseTexts(new Map());
    setLoadState({ kind: 'pending' });
  }

  const pageCount = Math.max(
    1,
    Math.ceil(familyHighlights.length / JOURNAL_PAGE_SIZE),
  );
  const boundedPage = Math.min(page, pageCount - 1);
  const pageStart = boundedPage * JOURNAL_PAGE_SIZE;
  const shown = useMemo(
    () => familyHighlights.slice(pageStart, pageStart + JOURNAL_PAGE_SIZE),
    [familyHighlights, pageStart],
  );

  // Distinct chapter keys touched by the visible rows (memoized so the ref only
  // changes when the visible slice changes — stable effect dep, no render loop).
  const neededKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const h of shown) keys.add(`${h.bookSlug}:${h.chapter}`);
    return Array.from(keys).sort();
  }, [shown]);

  // Resolve the verse text for every chapter the visible rows touch (detail
  // only). Driven by the revealed rows + the retry tick; re-resolving on paging
  // is a handful of fast IDB reads and replaces with a superset, so no row loses
  // its text and loadState never flashes pending mid-session.
  useEffect(() => {
    if (!detail || !isValidFamily || neededKeys.length === 0) return;
    let cancelled = false;
    void (async () => {
        const map = new Map<string, string>();
        const slugs = Array.from(
          new Set(neededKeys.map((key) => key.slice(0, key.lastIndexOf(':')))),
        );
        const results = await Promise.allSettled(
          slugs.map((slug) => ensureBookAvailable(slug)),
        );
        const books = new Map(
          results.flatMap((result, index) =>
            result.status === 'fulfilled'
              ? ([[slugs[index], result.value]] as const)
              : [],
          ),
        );
        for (const key of neededKeys) {
          if (cancelled) return;
          const sep = key.lastIndexOf(':');
          const slug = key.slice(0, sep);
          const ch = parseInt(key.slice(sep + 1), 10);
          const blocks = books.get(slug)?.chapters[String(ch)] ?? null;
          if (blocks) {
            for (const [v, txt] of chapterVerseTexts(blocks)) {
              map.set(`${slug}:${ch}:${v}`, txt);
            }
          }
        }
        if (cancelled) return;
        setVerseTexts(map);
        const failed = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        );
        setLoadState(
          failed
            ? {
                kind: 'failed',
                offline: isOfflineHydrationFailure(failed.reason),
              }
            : { kind: 'ready' },
        );
      })().catch((err) => {
        if (cancelled) return;
        setLoadState({ kind: 'failed', offline: isOfflineHydrationFailure(err) });
      });
    ensureBibleHydrated().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [detail, isValidFamily, neededKeys, retryTick]);

  useEffect(() => {
    const prior = document.title;
    document.title =
      detail && isValidFamily
        ? t(`bible.hlFamily.${family}`)
        : t('bible.journalMeaningTitle');
    return () => {
      document.title = prior;
    };
  }, [detail, isValidFamily, family, t]);

  if (!BIBLE_READER_V2_ENABLED)
    return <Navigate to="/bible/bookmarks" replace />;
  // An unknown family slug is static, safe to redirect pre-hydration.
  if (detail && !isValidFamily)
    return <Navigate to="/bible/bookmarks/meaning" replace />;

  // Wait for the source loads to settle before treating empty as truly empty
  // (a direct load / refresh can render before IDB hydrates; codex BLOCKER).
  if (!ready)
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
          <div
            ref={noticeReserveRef}
            className={styles.noticeReserve}
            style={{ height: noticeReserve }}
            data-notice-reserve={noticeReserve}
            aria-hidden="true"
          />
          <div className={styles.topBar}>
            <Link
              to={detail ? '/bible/bookmarks/meaning' : '/bible/bookmarks'}
              className={styles.backLink}
            >
              {t('bible.back')}
            </Link>
            <div className={styles.headerActions}>
              <BibleThemePicker />
            </div>
          </div>
          <h1 className={styles.title}>
            {detail
              ? t(`bible.hlFamily.${family}`)
              : t('bible.journalMeaningTitle')}
          </h1>
          <JournalSourceState state={sourceState} onRetry={retrySources} />
        </div>
      </div>
    );

  // ── Family list ────────────────────────────────────────────────────────────
  if (!detail) {
    // No highlights at all (the home only shows the lens when there are some;
    // this is a direct URL) -> back home.
    if (families.length === 0)
      return <Navigate to="/bible/bookmarks" replace />;
    return (
      <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
        <div className={styles.readerBg} aria-hidden="true" />
        <div className={styles.container} data-bible-theme={bibleTheme}>
        <div
          ref={noticeReserveRef}
          className={styles.noticeReserve}
          style={{ height: noticeReserve }}
          data-notice-reserve={noticeReserve}
          aria-hidden="true"
        />
        <div className={styles.topBar}>
          <Link to="/bible/bookmarks" className={styles.backLink}>
            {t('bible.back')}
          </Link>
          <div className={styles.headerActions}>
            <BibleThemePicker />
          </div>
        </div>
        <h1 className={styles.title}>{t('bible.journalMeaningTitle')}</h1>
        <p className={styles.subtitle}>{t('bible.journalMeaningSubtitle')}</p>
        <ul className={styles.familyList}>
          {families.map((fc) => (
            <li key={fc.family} className={styles.rowItem}>
              <Link
                to={`/bible/bookmarks/meaning/${fc.family}`}
                className={styles.familyRow}
                style={{
                  backgroundColor: `var(--reader-hl-${fc.family})`,
                  borderLeftColor: HL_DOT_HEX[fc.family],
                }}
              >
                <span
                  className={styles.familyDot}
                  style={{ backgroundColor: HL_DOT_HEX[fc.family] }}
                  aria-hidden="true"
                />
                <span className={styles.familyText}>
                  <span className={styles.familyName}>
                    {t(`bible.hlFamily.${fc.family}`)}
                  </span>
                  <span className={styles.familyDesc}>
                    {t(`bible.hlFamilyDesc.${fc.family}`)}
                  </span>
                </span>
                <span className={styles.familyCount}>
                  {t('bible.hlFamilyCount', { count: fc.count })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        </div>
      </div>
    );
  }

  // ── Family detail ──────────────────────────────────────────────────────────
  // A valid but empty family (e.g. its last highlight was just removed) -> back
  // to the family list, which in turn redirects home when empty.
  if (familyHighlights.length === 0)
    return <Navigate to="/bible/bookmarks/meaning" replace />;

  // The whole detail page is one family, so it wears that family's color: a dot
  // in the title, the rail on every row, and a faint wash behind each verse.
  const famColor = HL_DOT_HEX[family as HighlightFamily];

  return (
    <div className={styles.readerRoot} data-bible-theme={bibleTheme}>
      <div className={styles.readerBg} aria-hidden="true" />
      <div className={styles.container} data-bible-theme={bibleTheme}>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />
      <div className={styles.topBar}>
        <Link to="/bible/bookmarks/meaning" className={styles.backLink}>
          {t('bible.back')}
        </Link>
        <div className={styles.headerActions}>
          <BibleThemePicker />
        </div>
      </div>
      <h1 className={styles.title}>
        <span
          className={styles.titleDot}
          style={{ backgroundColor: famColor }}
          aria-hidden="true"
        />
        {t(`bible.hlFamily.${family}`)}
      </h1>
      <p className={styles.familyDescLine}>
        {t(`bible.hlFamilyDesc.${family}`)}
      </p>
      <p className={styles.subtitle}>
        {t('bible.hlFamilyCount', { count: familyHighlights.length })}
      </p>

      {loadState.kind === 'pending' && (
        <p className={styles.status}>{t('bible.chapterLoading')}</p>
      )}
      {loadState.kind === 'failed' && (
        <button
          type="button"
          className={styles.retryNotice}
          onClick={() => {
            setVerseTexts(new Map());
            setLoadState({ kind: 'pending' });
            setRetryTick((n) => n + 1);
          }}
        >
          {t(
            loadState.offline
              ? 'bible.offlineNeedsDownload'
              : 'bible.chapterReaderLoadFailed',
          )}
        </button>
      )}

      <ul className={styles.list}>
        {shown.map((hl) => {
          const fam = coerceFamily(hl.family) ?? 'plain';
          const text = verseTexts.get(`${hl.bookSlug}:${hl.chapter}:${hl.verse}`);
          return (
            <li key={hl.id} className={styles.rowItem}>
              <Link
                to={`/bible/bookmarks/book/${hl.bookSlug}/${hl.chapter}`}
                className={styles.row}
                style={{ borderLeftColor: famColor }}
              >
                <div className={styles.rowTop}>
                  <span className={styles.rowRef}>{hl.reference}</span>
                  <span className={styles.chip}>
                    <span
                      className={styles.chipDot}
                      style={{ backgroundColor: HL_DOT_HEX[fam] }}
                      aria-hidden="true"
                    />
                    {t(`bible.hlFamily.${fam}`)}
                  </span>
                </div>
                {text ? (
                  <p
                    className={`${styles.verseText} ${styles.verseTextWashed}`}
                    style={{ backgroundColor: `var(--reader-hl-${family})` }}
                  >
                    {text}
                  </p>
                ) : null}
              </Link>
            </li>
          );
        })}
      </ul>
      <JournalPagination
        page={boundedPage}
        totalItems={familyHighlights.length}
        onPageChange={setPage}
      />
      </div>
    </div>
  );
}
